/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — detail panel.
 *
 * The Vitals drawer body (the "full sparkline/battery detail" VitalsPage
 * defers). For the selected client it shows, per triage vital: the current
 * value + unit, the "±N% vs baseline" delta, and a 24h Sparkline with the
 * personal normal-range band (a "No trend" note when no history exists yet).
 * Below: the active vitals-alerts list, the device source + battery, and a
 * Generate SITRep action (hands off to Nora — see the SitRep engine story).
 *
 * Presentational: derives its descriptors from the pure vitalsPanelModel so it
 * stays a renderer. a11y: labelled regions; the SITRep control is a real
 * button, disabled when no handler is wired. Fail-safe: a null/partial row
 * degrades (value "—", no sparkline) instead of throwing. Colors from the
 * single design-token set; readings use green / yellow / red range severity.
 */
import React from "react";
import PropTypes from "prop-types";
import Sparkline from "./Sparkline";
import { panelVitals, panelDevice } from "../../pages/web/vitalsPanelModel";
import { theme, severity, severityToken } from "../../styles/designTokens";
import {
  srOnlyStyle,
  vitalRangeColor,
  vitalSeverityA11yLabel,
  vitalSeverityMarker,
} from "../../utils/vitalSeverity";

const label = { color: theme.textMuted, fontSize: 12, fontFamily: theme.fontUI };
const mono = { fontFamily: theme.fontMono };

export default function VitalsDetailPanel({ row, onGenerateSitRep }) {
  const vitals = panelVitals(row);
  const device = panelDevice(row);
  const alerts = row && Array.isArray(row.activeAlerts) ? row.activeAlerts.filter(Boolean) : [];
  const canSitRep = typeof onGenerateSitRep === "function";

  return (
    <div
      data-testid="vitals-detail-panel"
      style={{ display: "grid", gap: 16, color: theme.textPrimary, fontFamily: theme.fontUI }}
    >
      <section aria-label="Vitals trends" style={{ display: "grid", gap: 10 }}>
        {vitals.map((v) => {
          const accent =
            v.value === null
              ? theme.textPrimary
              : vitalRangeColor(v.severity) || theme.textPrimary;
          const marker = v.value === null ? "" : vitalSeverityMarker(v.severity);
          const a11ySeverity =
            v.value === null ? null : vitalSeverityA11yLabel(v.severity);
          return (
            <div
              key={v.key}
              data-testid={`panel-vital-${v.key}`}
              data-vital-severity={v.severity}
              style={{ display: "grid", gridTemplateColumns: "1fr auto 120px", gap: 12, alignItems: "center" }}
            >
              <div>
                <div style={label}>{v.label}</div>
                <div style={{ ...mono, color: accent, fontSize: 16 }}>
                  {v.value !== null ? `${v.value} ${v.unit}` : "—"}
                  {/* Severity is never color-only: marker + announced word. */}
                  {marker ? (
                    <span aria-hidden="true" style={{ marginLeft: 4, fontWeight: 700 }}>
                      {marker}
                    </span>
                  ) : null}
                  {a11ySeverity ? (
                    <span style={srOnlyStyle}>{a11ySeverity}</span>
                  ) : null}
                </div>
              </div>
              <div
                style={{
                  ...mono,
                  ...label,
                  // At-risk accent, not the clinical band color: an
                  // out-of-range baseline delta must not render green just
                  // because the absolute reading is in band.
                  color: v.outOfRange ? severity.atRisk.color : theme.textMuted,
                }}
              >
                {v.delta || ""}
              </div>
              {v.series.length >= 2 ? (
                <Sparkline
                  values={v.series}
                  band={v.band}
                  color={v.value !== null ? accent : undefined}
                  ariaLabel={`${v.label} 24-hour trend`}
                />
              ) : (
                <span data-testid={`panel-notrend-${v.key}`} style={label}>
                  No trend
                </span>
              )}
            </div>
          );
        })}
      </section>

      <section aria-label="Active vitals alerts" style={{ display: "grid", gap: 6 }}>
        <div style={label}>Active alerts</div>
        {alerts.length ? (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 4 }}>
            {alerts.map((a, i) => (
              <li
                key={a.id ?? i}
                style={{
                  // severityToken degrades unknown/missing severity to `normal`
                  // (not at-risk) so bad data can't falsely escalate the tint.
                  fontSize: 13,
                  color: severityToken(a.severity).color,
                }}
              >
                {a.reason || a.label || "Alert"}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ ...label, margin: 0 }}>No active alerts</p>
        )}
      </section>

      <div
        data-testid="panel-device"
        style={{ ...label, display: "flex", gap: 12, flexWrap: "wrap" }}
      >
        <span>Device: {device.source || "—"}</span>
        <span>Battery: {device.battery !== null ? `${device.battery}%` : "—"}</span>
      </div>

      <button
        type="button"
        data-testid="panel-sitrep"
        disabled={!canSitRep}
        onClick={canSitRep ? () => onGenerateSitRep(row) : undefined}
        style={{
          justifySelf: "start",
          minHeight: 36,
          padding: "6px 14px",
          borderRadius: 6,
          border: `1px solid ${severity.nora.color}`,
          background: theme.surface,
          color: canSitRep ? severity.nora.color : theme.textMuted,
          cursor: canSitRep ? "pointer" : "default",
          fontFamily: theme.fontUI,
          fontSize: 13,
        }}
      >
        Generate SITRep
      </button>
    </div>
  );
}

VitalsDetailPanel.propTypes = {
  // The selected triage row: { vitals, baselines, trends, device, activeAlerts }.
  row: PropTypes.object,
  // (row) => void. Absent -> the SITRep button renders disabled.
  onGenerateSitRep: PropTypes.func,
};
